import React, { useState } from 'react';
import { CreditCard, Plus, Shield, Lock, Trash2, Star, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { formatCurrency, getCardBrandIcon, getCardBrandName } from '@/utils/paymentUtils';
import CardInputForm from '@/components/payment/CardInputForm';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

const PaymentMethodsTab: React.FC = () => {
  const { user } = useAuth();
  const { paymentMethods, loading, error, refetch } = usePaymentMethods();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [updatingPrimaryId, setUpdatingPrimaryId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-white/70">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-400"></div>
          <span className="font-light">Loading payment methods...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
          <div className="text-white/80">
            <h3 className="font-medium">Unable to load payment methods</h3>
            <p className="text-sm text-white/60 mt-2">{error}</p>
          </div>
          <Button onClick={refetch} variant="outline" className="border-white/20 text-white/80 hover:text-white">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const handleSetPrimary = async (methodId: string) => {
    if (!user) {
      toast({
        title: 'Sign In Required',
        description: 'Please sign in to update your payment methods.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUpdatingPrimaryId(methodId);

      const { error: clearError } = await supabase
        .from('user_payment_methods')
        .update({ is_primary: false })
        .eq('user_id', user.id);

      if (clearError) throw clearError;

      const { error: updateError } = await supabase
        .from('user_payment_methods')
        .update({ is_primary: true })
        .eq('id', methodId)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      toast({
        title: 'Primary Method Updated',
        description: 'Your primary payment method has been updated successfully.'
      });

      await refetch();
    } catch (error: any) {
      console.error('Error setting primary payment method:', error);
      toast({
        title: 'Error',
        description: error?.message ?? 'Failed to update primary payment method.',
        variant: 'destructive'
      });
    } finally {
      setUpdatingPrimaryId(null);
    }
  };

  const handleRemoveMethod = (methodId: string, isPrimary: boolean) => {
    if (isPrimary) {
      toast({
        title: 'Primary Method Active',
        description: 'Set another card as primary before removing this payment method.',
        variant: 'destructive'
      });
      return;
    }

    setConfirmDeleteId(methodId);
  };

  const confirmDeleteMethod = async () => {
    if (!confirmDeleteId) return;

    if (!user) {
      toast({
        title: 'Sign In Required',
        description: 'Please sign in to manage payment methods.',
        variant: 'destructive'
      });
      setConfirmDeleteId(null);
      return;
    }

    try {
      setDeletingId(confirmDeleteId);

      const { error } = await supabase
        .from('user_payment_methods')
        .delete()
        .eq('id', confirmDeleteId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: 'Payment Method Removed',
        description: 'The payment method has been removed from your account.'
      });

      await refetch();
    } catch (error: any) {
      console.error('Error removing payment method:', error);
      toast({
        title: 'Error',
        description: error?.message ?? 'Failed to remove payment method.',
        variant: 'destructive'
      });
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-8 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <h2 className="text-2xl font-light text-white mb-2">Payment Methods</h2>
            <p className="text-white/70 font-light">Manage your secure payment options for bidding</p>
          </div>
          
          <Button 
            onClick={() => setShowAddCard(true)}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Payment Method
          </Button>
        </motion.div>

        {/* Payment Methods Grid */}
        {paymentMethods.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-white/5 backdrop-blur-xl border-white/20 border-dashed">
              <CardContent className="p-12 text-center">
                <CreditCard className="h-16 w-16 text-white/40 mx-auto mb-6" />
                <h3 className="text-xl font-light text-white mb-2">No Payment Methods</h3>
                <p className="text-white/60 font-light mb-6">Add a payment method to start bidding on artworks</p>
                <Button 
                  onClick={() => setShowAddCard(true)}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Payment Method
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {paymentMethods.map((method, index) => (
              <motion.div
                key={method.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card 
                  className={cn(
                    "bg-white/5 backdrop-blur-xl border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-[1.02]",
                    method.is_primary && "ring-2 ring-emerald-400/50 bg-emerald-500/10"
                  )}
                  onClick={() => setSelectedMethod(selectedMethod === method.id ? null : method.id)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl border border-white/20">
                          <CreditCard className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-white font-medium">
                            {getCardBrandName(method.card_type)}
                          </CardTitle>
                          <p className="text-white/60 text-sm font-light">
                            •••• •••• •••• {method.last_four}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {method.is_primary && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-400/30">
                            <Star className="h-3 w-3 mr-1" />
                            Primary
                          </Badge>
                        )}
                        {method.is_verified ? (
                          <CheckCircle className="h-5 w-5 text-green-400" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-yellow-400" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60 font-light">Expires</span>
                        <span className="text-white font-medium">
                          {String(method.expiry_month).padStart(2, '0')}/{method.expiry_year}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60 font-light">Cardholder</span>
                        <span className="text-white font-medium">{method.cardholder_name}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <Shield className="h-4 w-4 text-emerald-400" />
                        <span className="text-emerald-400 font-light">Secure & Encrypted</span>
                      </div>
                      
                      {selectedMethod === method.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="border-t border-white/10 pt-4 mt-4 space-y-2"
                        >
                          {!method.is_primary && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetPrimary(method.id);
                              }}
                              disabled={updatingPrimaryId === method.id}
                              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg"
                            >
                              {updatingPrimaryId === method.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <Star className="h-4 w-4 mr-2" />
                                  Set as Primary
                                </>
                              )}
                            </Button>
                          )}
                          
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMethod(method.id, method.is_primary);
                            }}
                            variant="outline"
                            size="sm"
                            className="w-full border-red-400/30 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            disabled={deletingId === method.id}
                          >
                            {deletingId === method.id ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Removing...
                              </>
                            ) : (
                              <>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remove Method
                              </>
                            )}
                          </Button>
                        </motion.div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Security Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
            <CardHeader>
              <CardTitle className="text-white font-light flex items-center gap-3">
                <Lock className="h-5 w-5 text-emerald-400" />
                Payment Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Encrypted Storage</p>
                      <p className="text-white/60 text-sm font-light">Your payment information is encrypted and stored securely</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">PCI Compliance</p>
                      <p className="text-white/60 text-sm font-light">We meet the highest standards for payment card security</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Fraud Protection</p>
                      <p className="text-white/60 text-sm font-light">Advanced fraud detection keeps your account safe</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Secure Transactions</p>
                      <p className="text-white/60 text-sm font-light">All payments are processed through secure channels</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Add Payment Method Dialog - Appears above dashboard */}
      <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
        <DialogContent className="bg-slate-900/95 backdrop-blur-xl border-white/20 text-white max-w-lg z-[9999]">
          <DialogHeader>
            <DialogTitle className="text-white font-light text-xl">Add New Payment Method</DialogTitle>
          </DialogHeader>
          <CardInputForm 
            onCardAdded={() => {
              console.log('✅ Payment method added successfully');
              setShowAddCard(false);
              setTimeout(() => {
                refetch();
              }, 500);
              toast({
                title: 'Payment Method Added',
                description: 'Your payment method has been added successfully!',
              });
            }}
            onCancel={() => {
              console.log('❌ Card input cancelled');
              setShowAddCard(false);
            }}
            estimatedBidAmount={0}
            showFeePreview={false}
            isRequired={false}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => {
        if (!open) {
          setConfirmDeleteId(null);
        }
      }}>
        <AlertDialogContent className="bg-slate-950/95 backdrop-blur-xl border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-white">
              <Trash2 className="h-4 w-4 text-red-400" />
              Remove Payment Method
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              This card will be permanently removed from your account. You can only delete non-primary payment methods.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white border-white/20 hover:bg-white/20">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMethod}
              className="bg-red-500 hover:bg-red-400 text-white"
              disabled={!!deletingId}
            >
              {deletingId ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Card'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScrollArea>
  );
};

export default PaymentMethodsTab;